import {
    Divider,
    ListItemIcon,
    ListItemText,
    Menu,
    MenuItem,
    alpha,
} from '@mui/material';
import { Fragment, ReactNode, memo } from 'react';

export type ActionMenuItemTone = 'default' | 'danger';
export type ActionMenuArrowPlacement = 'top' | 'left';

export interface ActionMenuItem {
    id: string;
    label: string;
    description?: string;
    icon?: ReactNode;
    tone?: ActionMenuItemTone;
    dividerBefore?: boolean;
    onClick: () => void;
    dataTestClass?: string;
    dataTestId?: string;
}

interface ActionMenuProps {
    anchorEl: HTMLElement | null;
    open: boolean;
    onClose: () => void;
    items: ActionMenuItem[];
    dataTestClass?: string;
    minWidth?: number | string;
    maxWidth?: number | string;
    arrowPlacement?: ActionMenuArrowPlacement;
}

const getPlacementConfig = (arrowPlacement: ActionMenuArrowPlacement) => {
    if (arrowPlacement === 'left') {
        return {
            anchorOrigin: {
                horizontal: 'right' as const,
                vertical: 'center' as const,
            },
            transformOrigin: {
                horizontal: 'left' as const,
                vertical: 'center' as const,
            },
            paperSx: {
                marginLeft: '8px',
                '&::before': {
                    content: '""',
                    position: 'absolute',
                    top: '50%',
                    left: '-7px',
                    width: 0,
                    height: 0,
                    borderStyle: 'solid',
                    borderWidth: '7px 7px 7px 0',
                    borderColor: 'transparent #ffffff transparent transparent',
                    transform: 'translateY(-50%)',
                    filter: 'drop-shadow(-2px 1px 1px rgba(0, 0, 0, 0.08))',
                },
            },
        };
    }

    return {
        anchorOrigin: {
            horizontal: 'right' as const,
            vertical: 'bottom' as const,
        },
        transformOrigin: {
            horizontal: 'right' as const,
            vertical: 'top' as const,
        },
        paperSx: {
            marginTop: '8px',
            '&::before': {
                content: '""',
                position: 'absolute',
                top: '-7px',
                right: '14px',
                width: 0,
                height: 0,
                borderStyle: 'solid',
                borderWidth: '0 7px 7px 7px',
                borderColor: 'transparent transparent #ffffff transparent',
                filter: 'drop-shadow(0px -1px 1px rgba(0, 0, 0, 0.08))',
            },
        },
    };
};

export const ActionMenu = memo(
    ({
        anchorEl,
        open,
        onClose,
        items,
        dataTestClass,
        minWidth = 200,
        maxWidth,
        arrowPlacement = 'top',
    }: ActionMenuProps) => {
        const placement = getPlacementConfig(arrowPlacement);

        return (
            <Menu
                anchorEl={anchorEl}
                open={open}
                onClose={onClose}
                data-testclass={dataTestClass}
                anchorOrigin={placement.anchorOrigin}
                transformOrigin={placement.transformOrigin}
                MenuListProps={{
                    sx: {
                        padding: '10px 0',
                    },
                }}
                slotProps={{
                    paper: {
                        elevation: 0,
                        sx: {
                            overflow: 'visible',
                            backgroundColor: 'common.white',
                            borderRadius: '8px',
                            minWidth,
                            maxWidth,
                            boxShadow: '0px 2px 12px rgba(0, 0, 0, 0.12)',
                            ...placement.paperSx,
                        },
                    },
                }}
            >
                {items.map((item) => {
                    const isDanger = item.tone === 'danger';
                    const hasDescription = Boolean(item.description);

                    return (
                        <Fragment key={item.id}>
                            {item.dividerBefore ? (
                                <Divider
                                    sx={{
                                        margin: '0 10px !important',
                                        borderColor: 'rgba(0, 0, 0, 0.12)',
                                    }}
                                />
                            ) : null}
                            <MenuItem
                                data-testclass={item.dataTestClass}
                                data-testid={item.dataTestId}
                                onClick={() => {
                                    item.onClick();
                                    onClose();
                                }}
                                sx={{
                                    alignItems: 'center',
                                    gap: '12px',
                                    padding: hasDescription
                                        ? '12px 16px'
                                        : '9px 16px',
                                    whiteSpace: 'normal',
                                    minHeight: '38px',
                                    '& .MuiListItemIcon-root': {
                                        minWidth: 'auto',
                                    },
                                }}
                            >
                                {item.icon ? (
                                    <ListItemIcon
                                        sx={{
                                            minWidth: 'auto',
                                            color: isDanger
                                                ? 'error.main'
                                                : (theme) =>
                                                      alpha(
                                                          theme.palette.text
                                                              .primary,
                                                          0.45,
                                                      ),
                                        }}
                                    >
                                        {item.icon}
                                    </ListItemIcon>
                                ) : null}
                                <ListItemText
                                    primary={item.label}
                                    secondary={item.description}
                                    primaryTypographyProps={{
                                        sx: {
                                            color: isDanger
                                                ? 'error.main'
                                                : 'text.primary',
                                        },
                                    }}
                                    secondaryTypographyProps={
                                        hasDescription
                                            ? {
                                                  variant: 'caption',
                                                  sx: {
                                                      color: (theme) =>
                                                          alpha(
                                                              theme.palette.text
                                                                  .primary,
                                                              0.55,
                                                          ),
                                                      marginTop: '2px',
                                                  },
                                              }
                                            : undefined
                                    }
                                />
                            </MenuItem>
                        </Fragment>
                    );
                })}
            </Menu>
        );
    },
);

ActionMenu.displayName = 'ActionMenu';
